import type { ReactNode } from 'react';
import ViennaBefore from '../components/mocks/ViennaBefore';
import ViennaAfter from '../components/mocks/ViennaAfter';
import ChefBefore from '../components/mocks/ChefBefore';
import ChefAfter from '../components/mocks/ChefAfter';

/*
 * NOTE: As of the live-preview refactor (Apr 2026), the case-study
 * section renders each project as an embedded `<SitePreview>` of the
 * actual live URL rather than the before/after slider. The `mock`
 * entries below are kept in place as a fallback: if embedding is ever
 * blocked (X-Frame-Options, mixed content, etc.) we can swap the viz
 * back to <BeforeAfter/> without touching the data layer.
 */

/**
 * Case studies — the data, lifted out of Transformation.tsx.
 *
 * Keeping this as a .tsx module (not JSON) so the `mock` entries can hold
 * actual React elements without a bespoke renderer. Adding a third case
 * later is now a 10-line change here with no component edits.
 *
 * Honesty rule: every claim under `wins` must be visually verifiable on
 * the live URL. No inferred metrics. No bullets we can't defend.
 */
export interface Case {
  id: string;
  kicker: string;
  title: string;
  titleEm: string;
  body: string;
  tags: string[];
  /** Each entry is [heading, body]. Both strings; no numeric claims. */
  wins: Array<[string, string]>;
  /** Bare host, rendered with https:// prefix at the call site. */
  url: string;
  /**
   * Full URL (with protocol) used for the embedded live preview AND for
   * the chrome's "Visit" button. Kept separate from `url` because the
   * display value should stay bare (`viennaaquariumandpets.com`) while
   * the iframe / link needs the real scheme.
   */
  embedUrl: string;
  /**
   * If set, the case renders a STATIC screenshot inside the browser
   * chrome instead of a live iframe. We do this when the target can't
   * be embedded reliably — e.g., http:// sites that hit mixed-content
   * blocking once WebNova is live on https. The "Visit" button still
   * sends the visitor to the real site.
   */
  staticImg?: string;
  /** 2x variant of `staticImg` for hi-DPI displays. */
  staticImg2x?: string;
  mock: { before: ReactNode; after: ReactNode };
  /** When true, the visual appears first (left) and info second (right). */
  flip: boolean;
}

export const CASES: Case[] = [
  {
    id: 'chef',
    kicker: 'Case 01 · Hospitality',
    title: 'Chef Zeitoun',
    titleEm: 'Levantine kitchen',
    body: "Chef Zeitoun has been cooking at a level most neighborhood restaurants don't reach. The old site didn't carry any of that forward — it read like a thousand other restaurant templates, with the menu buried three clicks deep. We rebuilt it around the food.",
    tags: ['Restaurant', 'Brand presentation', 'Menu & reservations'],
    wins: [
      [
        'Menu reads like a tasting, not a spreadsheet',
        'Named dishes, paced reading, editorial typography.',
      ],
      ['Photography is consistent', 'Light, tone, and framing unified across every dish.'],
      [
        'Reservation path is two taps',
        'Book button visible from any screen height, on any device.',
      ],
    ],
    url: 'chefzeitoun.com',
    embedUrl: 'https://chefzeitoun.com/',
    mock: { before: <ChefBefore />, after: <ChefAfter /> },
    flip: false,
  },
  {
    id: 'vienna',
    kicker: 'Case 02 · Retail',
    title: 'Vienna Aquarium & Pets',
    titleEm: 'twenty years on the block',
    body: "A real neighborhood pet store — the kind of place where the staff knows which fish you bought last winter. The old site didn't feel like that. Six colors, four fonts, a news column, a hit counter. We rebuilt it honestly: kept the warmth, cut the clutter.",
    tags: ['Retail', 'Information architecture', 'Mobile-first'],
    wins: [
      ['Fewer menu items, actually used', 'Top-level collapsed to the paths customers take.'],
      ['Visit info one tap away', 'Hours and directions surface from any page.'],
      [
        'Reads on a phone held one-handed',
        'Tap targets, line lengths, and contrast all rebuilt.',
      ],
    ],
    url: 'viennaaquariumandpets.com',
    // Vienna is http-only and would be blocked by mixed-content rules
    // once WebNova ships on https. Rather than gambling on what the
    // browser does in production, we render a real screenshot wrapped
    // in the same browser chrome the live Chef preview uses. Same
    // visual treatment, no embed risk. Visit still opens the live site.
    embedUrl: 'http://www.viennaaquariumandpets.com/',
    staticImg: '/brand/vienna-new-1200.jpg',
    staticImg2x: '/brand/vienna-new-2400.jpg',
    mock: { before: <ViennaBefore />, after: <ViennaAfter /> },
    flip: true,
  },
];
